// ABOUTME: Screen 2A Pick the Feeling: a carousel of feelings, each a full scene over the shared sea; confirm_feeling sends the focused one.
// ABOUTME: The carousel opens on the stored feeling (Excited when none), and browsing without confirming counts as unsent.

import { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { useCrossingContent } from '../../../hooks/useCrossingContent';
import { CROSSING_HEADLINE_INK } from '../../../theme/crossingTokens';
import { useFrameScale } from '../crossing/useFrameScale';
import { useHorizon } from '../crossing/useHorizon';
import { useCrossingReducedMotion } from '../crossing/useReducedMotion';
import ReadingCarousel from '../crossing/ReadingCarousel';
import type { ScreenProps, ScreenResult } from '../runner/screenContract';
import PickStatus from './carousel/PickStatus';
import { useCarouselIndex } from './carousel/useCarouselIndex';
import { useCarouselKeys } from './carousel/useCarouselKeys';
import FeelingScene from './feelings/FeelingScene';
import { FEELING_PLATE_WIDTH, feelingCopyBox } from './feelings/feelingCopyLayout';

export default function Screen2A({ crossing }: ScreenProps): ScreenResult {
  const { data: content } = useCrossingContent();
  const reducedMotion = useCrossingReducedMotion();
  const feelings = useMemo(() => Object.values(content?.feelings ?? {}), [content]);
  const { focused, index, count, prev, next, browsed } = useCarouselIndex(feelings, crossing.feeling, 'excited');
  useCarouselKeys(prev, next);
  const { line, frameH } = useHorizon();
  const { frameSc } = useFrameScale(FEELING_PLATE_WIDTH);
  const copy = feelingCopyBox({ line, frameSc, frameH });

  // The text and arrows sit up top; the focused feeling's scene is drawn behind them, standing on the horizon.
  const layer = (
    <Box
      sx={{
        position: 'relative', zIndex: 1, flex: 1, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'flex-start', gap: 2, p: 3, textAlign: 'center',
      }}
    >
      {/* The copy is a fixed block from under the header to just above the feeling scene's name, so it stays clear of the scene. */}
      <Box
        sx={{
          position: 'fixed', left: 0, right: 0, top: copy.top, bottom: copy.bottom, px: 3, pointerEvents: 'none',
          display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center', textAlign: 'center',
        }}
      >
        <Typography data-testid="feeling-eyebrow" sx={{ flexShrink: 0, fontSize: '16px', fontWeight: 900, letterSpacing: '2px', color: CROSSING_HEADLINE_INK }}>
          WHAT FEELING SHOWED UP?
        </Typography>
        {focused && (
          <Typography key={focused.code} data-testid="feeling-question" sx={{ flexShrink: 0, mt: 2, fontSize: '20px', lineHeight: 1.25, color: CROSSING_HEADLINE_INK }}>
            {focused.question}
          </Typography>
        )}
        <Box sx={{ flex: '0 1 28px', minHeight: 0, flexShrink: 1 }} />
      </Box>
      {count > 0 && (
        <ReadingCarousel
          items={feelings}
          activeIndex={index}
          reducedMotion={reducedMotion}
          renderItem={feeling => <FeelingScene feeling={feeling} />}
        />
      )}
      <PickStatus name={focused?.name} index={index} count={count} />
    </Box>
  );

  return {
    layer,
    headline: 'Use the arrows to see which feeling showed up first.',
    ...(focused && { picker: { label: focused.name, prevLabel: 'Previous feeling', nextLabel: 'Next feeling', onPrev: prev, onNext: next } }),
    slots: {
      confirm_feeling: { disabled: !focused, values: focused ? { feeling: focused.code } : undefined },
    },
    hasUnsentState: browsed && !!focused && focused.code !== crossing.feeling,
  };
}
